import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EMPTY_PROMO = {
  code: '',
  discountType: 'percentage',
  discountValue: '',
  limit: '',
  expires: '',
  status: 'Active',
  description: '',
};

let nextPromoId = 10;

const PromotionalTools = () => {
  const [promotions, setPromotions] = useState([
    {
      id: 1,
      code: 'SUMMER2026',
      discountType: 'percentage',
      discountValue: '20',
      status: 'Active',
      used: 45,
      limit: 100,
      expires: '2026-08-31',
      description: 'Summer sale discount',
    },
    {
      id: 2,
      code: 'WELCOME50',
      discountType: 'fixed',
      discountValue: '50',
      status: 'Active',
      used: 128,
      limit: 500,
      expires: '2026-12-31',
      description: 'New user welcome discount',
    },
  ]);

  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState(EMPTY_PROMO);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const openCreate = () => {
    setEditingPromo(null);
    setFormData(EMPTY_PROMO);
    setShowModal(true);
  };

  const openEdit = (promo) => {
    setEditingPromo(promo);
    setFormData({
      code: promo?.code,
      discountType: promo?.discountType,
      discountValue: String(promo?.discountValue),
      limit: String(promo?.limit || ''),
      expires: promo?.expires || '',
      status: promo?.status,
      description: promo?.description || '',
    });
    setShowModal(true);
  };

  const handleSave = (e) => {
    e?.preventDefault();
    if (editingPromo) {
      setPromotions(prev =>
        prev?.map(p =>
          p?.id === editingPromo?.id
            ? {
                ...p,
                code: formData?.code?.toUpperCase(),
                discountType: formData?.discountType,
                discountValue: formData?.discountValue,
                limit: formData?.limit ? parseInt(formData?.limit) : null,
                expires: formData?.expires,
                status: formData?.status,
                description: formData?.description,
              }
            : p
        )
      );
    } else {
      nextPromoId += 1;
      setPromotions(prev => [
        ...prev,
        {
          id: nextPromoId,
          code: formData?.code?.toUpperCase(),
          discountType: formData?.discountType,
          discountValue: formData?.discountValue,
          status: formData?.status,
          used: 0,
          limit: formData?.limit ? parseInt(formData?.limit) : null,
          expires: formData?.expires,
          description: formData?.description,
        },
      ]);
    }
    setShowModal(false);
  };

  const handleDelete = (id) => {
    setPromotions(prev => prev?.filter(p => p?.id !== id));
    setDeleteConfirm(null);
  };

  const handleToggleStatus = (id) => {
    setPromotions(prev =>
      prev?.map(p =>
        p?.id === id ? { ...p, status: p?.status === 'Active' ? 'Paused' : 'Active' } : p
      )
    );
  };

  const formatDiscount = (promo) => {
    if (promo?.discountType === 'percentage') return `${promo?.discountValue}%`;
    return `$${promo?.discountValue}`;
  };

  const activeCount = promotions?.filter(p => p?.status === 'Active')?.length;
  const totalUsed = promotions?.reduce((sum, p) => sum + (p?.used || 0), 0);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active Promotions</p>
              <p className="text-2xl font-bold text-foreground mt-1">{activeCount}</p>
            </div>
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Tag" size={24} className="text-primary" />
            </div>
          </div>
        </div>
        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Redemptions</p>
              <p className="text-2xl font-bold text-foreground mt-1">{totalUsed}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={24} className="text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Campaigns</p>
              <p className="text-2xl font-bold text-foreground mt-1">{promotions?.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="Megaphone" size={24} className="text-blue-600" />
            </div>
          </div>
        </div>
      </div>
      {/* Table */}
      <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Promotional Campaigns</h3>
            <p className="text-sm text-muted-foreground mt-1">Manage discount codes and limited-time offers</p>
          </div>
          <Button onClick={openCreate} className="flex items-center gap-2">
            <Icon name="Plus" size={18} />
            Create Promotion
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Discount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Usage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Expires</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {promotions?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <Icon name="Tag" size={40} className="mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No promotions yet. Create your first one!</p>
                  </td>
                </tr>
              ) : (
                promotions?.map(promo => (
                  <tr key={promo?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <span className="font-mono font-medium text-foreground">{promo?.code}</span>
                        {promo?.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{promo?.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-lg font-semibold text-primary">{formatDiscount(promo)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-muted-foreground capitalize">
                        {promo?.discountType === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {promo?.limit ? (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-muted rounded-full h-2 max-w-[80px]">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{ width: `${Math.min(((promo?.used || 0) / promo?.limit) * 100, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground">{promo?.used || 0}/{promo?.limit}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">{promo?.used || 0} / ∞</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-muted-foreground">{promo?.expires || '—'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(promo?.id)}
                        title="Click to toggle status"
                        className={`px-2 py-1 text-xs rounded-full font-medium cursor-pointer transition-colors ${
                          promo?.status === 'Active' ?'bg-green-100 text-green-800 hover:bg-green-200' :'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                        }`}
                      >
                        {promo?.status}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(promo)}
                          title="Edit promotion"
                        >
                          <Icon name="Edit" size={16} />
                        </Button>
                        {deleteConfirm === promo?.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDelete(promo?.id)}
                              className="px-2 py-1 text-xs bg-destructive text-white rounded hover:bg-destructive/90"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="px-2 py-1 text-xs bg-muted text-foreground rounded hover:bg-muted/80"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm(promo?.id)}
                            className="text-destructive hover:text-destructive/80"
                            title="Delete promotion"
                          >
                            <Icon name="Trash2" size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                {editingPromo ? 'Edit Promotion' : 'Create Promotion'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4">
              <Input
                label="Promo Code *"
                value={formData?.code}
                onChange={(e) => setFormData(p => ({ ...p, code: e?.target?.value?.toUpperCase() }))}
                placeholder="e.g., SUMMER2026"
                required
              />

              <Input
                label="Description"
                value={formData?.description}
                onChange={(e) => setFormData(p => ({ ...p, description: e?.target?.value }))}
                placeholder="Brief description of this promotion"
              />

              <Select
                label="Discount Type"
                value={formData?.discountType}
                onChange={(e) => setFormData(p => ({ ...p, discountType: e?.target?.value }))}
                options={[
                  { value: 'percentage', label: 'Percentage (%)' },
                  { value: 'fixed', label: 'Fixed Amount ($)' },
                ]}
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Discount Value *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    {formData?.discountType === 'percentage' ? '%' : '$'}
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData?.discountValue}
                    onChange={(e) => setFormData(p => ({ ...p, discountValue: e?.target?.value }))}
                    placeholder={formData?.discountType === 'percentage' ? '20' : '50'}
                    required
                    className="w-full pl-8 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Usage Limit</label>
                  <input
                    type="number"
                    min="0"
                    value={formData?.limit}
                    onChange={(e) => setFormData(p => ({ ...p, limit: e?.target?.value }))}
                    placeholder="Unlimited"
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Expiry Date</label>
                  <input
                    type="date"
                    value={formData?.expires}
                    onChange={(e) => setFormData(p => ({ ...p, expires: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground text-sm"
                  />
                </div>
              </div>

              <Select
                label="Status"
                value={formData?.status}
                onChange={(e) => setFormData(p => ({ ...p, status: e?.target?.value }))}
                options={[
                  { value: 'Active', label: 'Active' },
                  { value: 'Paused', label: 'Paused' },
                ]}
              />

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingPromo ? 'Update Promotion' : 'Create Promotion'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromotionalTools;
